import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { onContactLinkClick } from '@/lib/contactLinks';

export const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="terms-of-service-page pt-20 bg-black">
      {/* Hero Section */}
      <section className="hero py-24 bg-gradient-to-br from-black via-gray-900 to-black relative overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-20" />
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8 group">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </Link>
          
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-red-600/20 to-red-700/20 rounded-2xl flex items-center justify-center">
              <FileText className="w-8 h-8 text-red-500" />
            </div>
            <div>
              <h1 className="text-6xl font-bold text-white">Terms of Service</h1>
              <p className="text-gray-400 mt-2">Effective Date: 10 April 2025</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="content py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-invert prose-lg max-w-none">
            
            {/* Section 1 */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">1. Acceptance of Terms</h2>
              <p className="text-gray-300 leading-relaxed">
                These Terms of Service ("Terms") govern your access to and use of the website located at radiusconsulting.co.za (the "Website") and any services offered by Radius Consulting (Pty) Ltd ("Radius Consulting", "we", "us", or "our"), a brand marketing and growth strategy firm based in Johannesburg, South Africa. By accessing our Website, submitting an enquiry, engaging our services, or downloading any content, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Website or services.
              </p>
            </div>

            {/* Section 2 */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">2. Our Services</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Radius Consulting provides brand marketing and growth strategy services to businesses, including but not limited to:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li>Brand strategy and positioning</li>
                <li>Marketing strategy and campaign development</li>
                <li>Growth consulting and advisory</li>
                <li>Content strategy and creative direction</li>
                <li>Portfolio and case study development</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                The specific scope, deliverables, timelines, and fees for any engagement will be set out in a separate written agreement or proposal between Radius Consulting and the client.
              </p>
            </div>

            {/* Section 3 */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">3. Use of the Website</h2>
              
              <h3 className="text-2xl font-semibold text-white mb-4">3.1 Permitted use</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                You may use our Website for lawful purposes only. You agree not to:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-6">
                <li>Use the Website in any way that violates applicable local, national, or international laws or regulations</li>
                <li>Transmit any unsolicited or unauthorised advertising or promotional material</li>
                <li>Attempt to gain unauthorised access to any part of the Website or its related systems</li>
                <li>Engage in any conduct that restricts or inhibits anyone's use or enjoyment of the Website</li>
                <li>Introduce any viruses, malware, or other harmful material to the Website</li>
              </ul>

              <h3 className="text-2xl font-semibold text-white mb-4">3.2 Intellectual property</h3>
              <p className="text-gray-300 leading-relaxed">
                All content on this Website, including but not limited to text, graphics, logos, images, and downloadable materials, is the property of Radius Consulting or its content suppliers and is protected by applicable intellectual property laws. You may not reproduce, distribute, modify, or create derivative works from any content on this Website without our prior written consent.
              </p>
            </div>

            {/* Section 4 */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">4. Enquiries and Communications</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                When you submit a contact form, newsletter sign-up, or resource download request on our Website, you acknowledge that:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li>The information you provide is accurate and complete</li>
                <li>You are authorised to provide such information</li>
                <li>You consent to Radius Consulting using this information to respond to your enquiry or provide requested content</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                Submission of an enquiry does not constitute a binding contract or guarantee of services. We aim to respond to all enquiries within two business days. Response times may vary depending on the nature and volume of enquiries received.
              </p>
            </div>

            {/* Section 5 */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">5. Newsletter and Marketing Communications</h2>
              <p className="text-gray-300 leading-relaxed">
                By subscribing to our newsletter, you consent to receiving periodic marketing communications from Radius Consulting, including industry insights, company updates, and promotional content. You may unsubscribe from our newsletter at any time by clicking the unsubscribe link in any email communication or by contacting us directly at hello@radiusconsulting.co.za. We will process your unsubscribe request promptly.
              </p>
            </div>

            {/* Section 6 */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">6. Downloadable Resources</h2>
              <p className="text-gray-300 leading-relaxed">
                We may make guides, templates, and other resources available for download on our Website. These materials are provided for informational purposes only and do not constitute professional advice. Radius Consulting makes no warranties as to the completeness, accuracy, or suitability of these materials for any particular purpose. Downloadable resources are for your personal or internal business use only and may not be resold, redistributed, or published without our express written consent.
              </p>
            </div>

            {/* Section 7 */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">7. Client Engagements</h2>
              
              <h3 className="text-2xl font-semibold text-white mb-4">7.1 Proposals and agreements</h3>
              <p className="text-gray-300 leading-relaxed mb-6">
                Any formal engagement for services will be governed by a separate written proposal, statement of work, or service agreement agreed upon by both parties. These Terms apply in conjunction with any such agreement. In the event of a conflict, the specific terms of the written engagement agreement shall prevail.
              </p>

              <h3 className="text-2xl font-semibold text-white mb-4">7.2 Payment</h3>
              <p className="text-gray-300 leading-relaxed mb-6">
                Payment terms, rates, and invoicing schedules will be set out in the applicable engagement agreement. Unless otherwise agreed, invoices are payable within 30 days of the invoice date. Late payments may attract interest at the applicable legal rate.
              </p>

              <h3 className="text-2xl font-semibold text-white mb-4">7.3 Confidentiality</h3>
              <p className="text-gray-300 leading-relaxed">
                Both parties agree to treat as confidential any proprietary or sensitive information disclosed during the course of an engagement. This obligation survives termination of the engagement for a period of two years.
              </p>
            </div>

            {/* Section 8 */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">8. Disclaimer of Warranties</h2>
              <p className="text-gray-300 leading-relaxed">
                Our Website and its content are provided on an "as is" and "as available" basis without any warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement. We do not warrant that the Website will be uninterrupted, error-free, or free of viruses or other harmful components. We reserve the right to modify, suspend, or discontinue the Website at any time without notice.
              </p>
            </div>

            {/* Section 9 */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">9. Limitation of Liability</h2>
              <p className="text-gray-300 leading-relaxed">
                To the fullest extent permitted by law, Radius Consulting shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of, or inability to use, our Website or services, even if we have been advised of the possibility of such damages. Our total liability to you in connection with these Terms or our services shall not exceed the total fees paid by you to Radius Consulting in the three months preceding the claim.
              </p>
            </div>

            {/* Section 10 */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">10. Third-Party Links and Content</h2>
              <p className="text-gray-300 leading-relaxed">
                Our Website may contain links to third-party websites or resources. These links are provided for your convenience only. We do not endorse, control, or assume responsibility for the content, privacy practices, or availability of any third-party websites. Your use of any linked website is at your own risk.
              </p>
            </div>

            {/* Section 11 */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">11. Governing Law and Jurisdiction</h2>
              <p className="text-gray-300 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the Republic of South Africa. Any disputes arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of South Africa.
              </p>
            </div>

            {/* Section 12 */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">12. Changes to These Terms</h2>
              <p className="text-gray-300 leading-relaxed">
                We reserve the right to amend these Terms at any time. Updated Terms will be posted on this page with a revised effective date. Your continued use of our Website following any changes constitutes your acceptance of the revised Terms. We encourage you to review these Terms periodically.
              </p>
            </div>

            {/* Section 13 - Contact */}
            <div className="mb-12 bg-gradient-to-br from-gray-800 to-black rounded-2xl p-8 border border-gray-700">
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <Mail className="w-8 h-8 text-red-500" />
                13. Contact Us
              </h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="text-gray-300 space-y-2">
                <p><strong className="text-white">Radius Consulting (Pty) Ltd</strong></p>
                <p>Johannesburg, South Africa</p>
                <p>Email: <a href="mailto:mishaan@radiusconsulting.co.za" onClick={onContactLinkClick} className="text-red-500 hover:text-red-400">mishaan@radiusconsulting.co.za</a></p>
                <p>Website: <a href="https://radiusconsulting.co.za" className="text-red-500 hover:text-red-400">radiusconsulting.co.za</a></p>
              </div>
            </div>

            {/* Footer */}
            <div className="text-center text-gray-500 text-sm border-t border-gray-800 pt-8">
              © 2026 Radius Consulting (Pty) Ltd. All rights reserved.
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-16 bg-black border-t border-gray-800">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-3xl font-bold text-white mb-6">Ready to Get Started?</h3>
          <p className="text-xl text-gray-400 mb-8">
            Let's discuss how RADIUS can help grow your business.
          </p>
          <Link to="/contact">
            <Button size="lg" className="btn-primary">
              Contact Us
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
